import {
  Text,
  TextProps,
  Box,
  BoxProps,
  Checkbox,
  Stack,
  StackProps,
  useCheckbox,
  useCheckboxGroup,
  UseCheckboxGroupProps,
  UseCheckboxProps,
  useId,
  useStyleConfig,
  Spinner,
} from "@chakra-ui/react";
import moment from "moment-timezone";
import {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  useMemo,
} from "react";

export function ErrorMessage(props: TextProps) {
  return <Text {...props} color="red"></Text>;
}

type CheckboxCardGroupProps = StackProps & UseCheckboxGroupProps;

export const CheckboxCardGroup = (props: CheckboxCardGroupProps) => {
  const { children, defaultValue, value, onChange, ...rest } = props;
  const { getCheckboxProps } = useCheckboxGroup({
    defaultValue,
    value,
    onChange,
  });

  const cards = useMemo(
    () =>
      Children.toArray(children)
        .filter<ReactElement<RadioCardProps>>(isValidElement)
        .map((card) => {
          return cloneElement(card, {
            checkboxProps: getCheckboxProps({
              value: card.props.value,
            }),
          });
        }),
    [children, getCheckboxProps],
  );

  return <Stack {...rest}>{cards}</Stack>;
};

interface RadioCardProps extends BoxProps {
  value: string;
  checkboxProps?: UseCheckboxProps;
}

export const CheckboxCard = (props: RadioCardProps) => {
  const { checkboxProps, children, ...rest } = props;
  const { getInputProps, getCheckboxProps, getLabelProps, state } =
    useCheckbox(checkboxProps);
  const id = useId(undefined, "checkbox-card");
  const styles = useStyleConfig("RadioCard", props);

  return (
    <Box
      as="label"
      cursor="pointer"
      {...getLabelProps()}
      sx={{
        ".focus-visible + [data-focus]": {
          boxShadow: "outline",
          zIndex: 1,
        },
      }}
    >
      <input {...getInputProps()} aria-labelledby={id} />
      <Box sx={styles} {...getCheckboxProps()} {...rest}>
        <Stack direction="row">
          <Box flex="1">{children}</Box>
          <Checkbox
            pointerEvents="none"
            isFocusable={false}
            isChecked={state.isChecked}
            alignSelf="start"
          />
        </Stack>
      </Box>
    </Box>
  );
};

export const LoadingSpinner = () => (
  <Spinner
    thickness="4px"
    speed="0.65s"
    emptyColor="gray.200"
    color="blue.500"
    size="xl"
    margin={"0 auto"}
  />
);

export const countryList = [
  "Afghanistan",
  "Albania",
  "Algeria",
  "American Samoa",
  "Andorra",
  "Angola",
  "Anguilla",
  "Antarctica",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Aruba",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas (the)",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bermuda",
  "Bhutan",
  "Bolivia (Plurinational State of)",
  "Bonaire, Sint Eustatius and Saba",
  "Bosnia and Herzegovina",
  "Botswana",
  "Bouvet Island",
  "Brazil",
  "British Indian Ocean Territory (the)",
  "Brunei Darussalam",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cabo Verde",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Cayman Islands (the)",
  "Central African Republic (the)",
  "Chad",
  "Chile",
  "China",
  "Christmas Island",
  "Cocos (Keeling) Islands (the)",
  "Colombia",
  "Comoros (the)",
  "Congo (the Democratic Republic of the)",
  "Congo (the)",
  "Cook Islands (the)",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Curaçao",
  "Cyprus",
  "Czechia",
  "Côte d'Ivoire",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic (the)",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini",
  "Ethiopia",
  "Falkland Islands (the) [Malvinas]",
  "Faroe Islands (the)",
  "Fiji",
  "Finland",
  "France",
  "French Guiana",
  "French Polynesia",
  "French Southern Territories (the)",
  "Gabon",
  "Gambia (the)",
  "Georgia",
  "Germany",
  "Ghana",
  "Gibraltar",
  "Greece",
  "Greenland",
  "Grenada",
  "Guadeloupe",
  "Guam",
  "Guatemala",
  "Guernsey",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Heard Island and McDonald Islands",
  "Holy See (the)",
  "Honduras",
  "Hong Kong",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran (Islamic Republic of)",
  "Iraq",
  "Ireland",
  "Isle of Man",
  "Israel",
  "Italy",
  "Jamaica",
  "Japan",
  "Jersey",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Korea (the Democratic People's Republic of)",
  "Korea (the Republic of)",
  "Kuwait",
  "Kyrgyzstan",
  "Lao People's Democratic Republic (the)",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Macao",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands (the)",
  "Martinique",
  "Mauritania",
  "Mauritius",
  "Mayotte",
  "Mexico",
  "Micronesia (Federated States of)",
  "Moldova (the Republic of)",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Montserrat",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands (the)",
  "New Caledonia",
  "New Zealand",
  "Nicaragua",
  "Niger (the)",
  "Nigeria",
  "Niue",
  "Norfolk Island",
  "Northern Mariana Islands (the)",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine, State of",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines (the)",
  "Pitcairn",
  "Poland",
  "Portugal",
  "Puerto Rico",
  "Qatar",
  "Republic of North Macedonia",
  "Romania",
  "Russian Federation (the)",
  "Rwanda",
  "Réunion",
  "Saint Barthélemy",
  "Saint Helena, Ascension and Tristan da Cunha",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Martin (French part)",
  "Saint Pierre and Miquelon",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Sint Maarten (Dutch part)",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Georgia and the South Sandwich Islands",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan (the)",
  "Suriname",
  "Svalbard and Jan Mayen",
  "Sweden",
  "Switzerland",
  "Syrian Arab Republic",
  "Taiwan",
  "Tajikistan",
  "Tanzania, United Republic of",
  "Thailand",
  "Timor-Leste",
  "Togo",
  "Tokelau",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Turks and Caicos Islands (the)",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates (the)",
  "United Kingdom of Great Britain and Northern Ireland (the)",
  "United States Minor Outlying Islands (the)",
  "United States of America (the)",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Venezuela (Bolivarian Republic of)",
  "Viet Nam",
  "Virgin Islands (British)",
  "Virgin Islands (U.S.)",
  "Wallis and Futuna",
  "Western Sahara",
  "Yemen",
  "Zambia",
  "Zimbabwe",
  "Åland Islands",
];

export function ToLocalDate(d: string) {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return moment.utc(d).tz(tz).format("YYYY-MM-DD HH:mm");
}

export function download_csv_sample(resource: string) {
  var newWindow = window.open();
  if (newWindow == null) return;
  newWindow.stop();

  var columns: Array<string> = [];
  var data: Array<Array<string>> = [];
  var extra_notes: Array<string> = [];

  switch (resource) {
    case "tubes":
      columns = [
        "number",
        "part_number",
        "flow_rate",
        "kind",
        "state",
        "notes",
        "kit",
      ];
      data = [
        ["454646", "", "50", "calibration", "", "some note", ""],
        ["11111111", "125", "100", "sampling", "in kit", "", "456789"],
        ["666454646", "11", "10", "research", "", "another note", ""],
        ["11111111", "125", "100", "sampling", "in analysis", "", ""],
        ["11111111", "125", "100", "sampling", "others", "details in note", ""],
      ];
      const kinds = ["calibration", "sampling", "research"];
      const states = [
        "in fridge without sample",
        "in kit",
        "in analysis",
        "in fridge with sample",
        "others",
      ];
      extra_notes = [
        `Kind should be one of <pre>${kinds.join(", ")}</pre>`,
        `State should be one of: <pre>${states.join(", ")}</pre>`,
        `State only applies to sampling tubes`,
      ];

      break;

    case "kits":
      columns = [
        "site",
        "number",
        "make_date",
        "bag_conditioned",
        "notes",
        "tubes",
      ];
      data = [
        [
          "Shirley",
          "123456",
          "2020-10-10",
          "2023-07-19",
          "",
          "123456789,987765",
        ],
        ["Licester", "456789", "2021-11-01", "2023-07-19", "some note", ""],
      ];
      break;

    case "pumps":
      columns = ["site", "name", "number", "charger", "notes"];
      data = [
        ["Shirley", "Pump1", "12333", "XYZ123", ""],
        ["Shirley", "Pump22", "87755", "", ""],
      ];
      break;

    case "sites":
      columns = [
        "code",
        "name",
        "project_name",
        "notes",
        "address1",
        "address2",
        "city",
        "state",
        "country",
        "postal_code",
      ];
      data = [
        [
          "SDX",
          "Shirley's Home Lab",
          "TB Project",
          "Seattle's office",
          "Somewhere",
          "",
          "Seattle",
          "WA",
          "USA",
          "12345",
        ],
        [
          "SDX",
          "Shirley's Overseas Lab",
          "TB Project",
          "",
          "Somewhere",
          "",
          "Leicester",
          "",
          "UK",
          "12345",
        ],
      ];
      break;

    default:
      return;
  }

  newWindow?.document.write(`<html>
<head>
<title>Example ${resource} csv</title>
<style>
td, th {
    border: 1px solid black;
    padding: 10px;
}
table {
    border-collapse: collapse;
}
</style>
</head>
<body>
<table>
<thead>
  <tr>
${columns.map((v) => `<th>${v}</th>`).join("")}
  </tr>
</thead>
${data
  .map((v) => `<tr>${v.map((vv) => `<td>${vv}</td>`).join("")}</tr>`)
  .join("")}
</table>
${
  extra_notes.length !== 0
    ? '<p style="font-weight: bold">Extra Notes:</p><ul>'
    : ""
}
${extra_notes.map((v) => `<li style="font-weight: bold">${v}</li>`).join("")}
${extra_notes.length !== 0 ? "</ul>" : ""}
</body>
</html>
`);
}
